import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { awsExports } from "../../aws-exports";
import { Authenticator, translations } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import {
  signOut,
  fetchAuthSession,
  getCurrentUser,
} from "aws-amplify/auth";
import { Hub, I18n } from "aws-amplify/utils";
import useAuth from "../../hooks/useAuth";

const oauth = {
  domain: "https://vistaflow.auth.eu-west-3.amazoncognito.com",
  scope: [
    "phone",
    "email",
    "profile",
    "openid",
    "aws.cognito.signin.user.admin",
    "ALL/ALL",
  ],
  responseType: "code",
};

Amplify.configure({
  Auth: {
    Cognito: {
      loginWith: {
        oauth: { oauth },
      },
      mandatorySignIn: true,
      region: awsExports.REGION,
      userPoolId: awsExports.USER_POOL_ID,
      userPoolWebClientId: awsExports.USER_POOL_APP_CLIENT_ID,
      userPoolClientId: awsExports.USER_POOL_APP_CLIENT_ID,
      scope: ["ALL/ALL"],
      signInConfig: {
        isSignUpDisplayed: false,
      },
    },
  },
});

function Login() {
  const [jwtToken, setJwtToken] = useState("");
  const [isTokenLoading, setIsTokenLoading] = useState(true);
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/orders";
  const [hasFetchedUser, setHasFetchedUser] = useState(false);

  I18n.putVocabularies(translations);
  I18n.putVocabulariesForLanguage("fr", {
    Email: "E-mail",
    Password: "Mot de passe",
    "Forgot your password?": "Mot de passe oublié ?",
  });
  I18n.setLanguage("fr");

  useEffect(() => {
    if (!hasFetchedUser) {
      const fetchUser = async () => {
        try {
          const user = await getCurrentUser();
          setAuth((prev) => ({ ...prev, userFromCognito: user }));
        } catch (err) {
          console.log("Pas d'utilisateur authentifié :", err);
        } finally {
          setHasFetchedUser(true);
        }
      };
      fetchUser();
    }
  }, [hasFetchedUser, setAuth]);

  const fetchJwtToken = useCallback(async () => {
    setIsTokenLoading(true);
    try {
      const tokens = (await fetchAuthSession())?.tokens;
      if (tokens?.accessToken) {
        const accessToken = tokens.accessToken.toString();
        const clientId = tokens.idToken?.payload?.profile
          ? String(tokens.idToken.payload.profile)
          : null;
        
        setAuth((prev) => ({
          ...prev,
          accessToken,
          clientId,
        }));
      }
      setJwtToken(accessToken);
    } catch (error) {
      console.log("Erreur lors de la récupération du JWT :", error);
    } finally {
      setIsTokenLoading(false);
    }
  }, [setAuth]);

  useEffect(() => {
    const listener = (data) => {
      if (data.payload.event === "signedIn") {
        fetchJwtToken();
      }
    };
    const unsubscribe = Hub.listen("auth", listener);
    return () => unsubscribe();
  }, [fetchJwtToken]);

  useEffect(() => {
    if (auth.accessToken) {
      navigate(from, { replace: true });
    }
  }, [auth.accessToken, from, navigate]);

  const handleLogOut = async () => {
    try {
      await signOut();
      setAuth({});
      navigate("/login", { replace: true });
    } catch (error) {
      console.log("Erreur lors de la déconnexion :", error);
    }
  };

  return (
    <Authenticator initialState="signIn" hideSignUp={true} loginMechanisms={['email']}>
      {({ user }) => (
        <div>
          <h2>Bienvenue {user.username}</h2>
          <button onClick={handleLogOut}>Se déconnecter</button>
          <div>
            <p>Informations d'authentification :</p>
            <pre>{JSON.stringify(auth, null, 2)}</pre>
          </div>
          <h4>Your JWT token:</h4>
          <div>{!isTokenLoading && <div>{jwtToken} </div>}</div>
        </div>
      )}
    </Authenticator>
  );
}

export default Login;
